import clsx from 'clsx';
import { FormControlLabel, Typography, ButtonBase } from '@material-ui/core';
import { PRODUCTS } from 'cart-model';
import { EventType } from 'common-event';
import { Fund, Risk } from 'funds-model';
import React, { FunctionComponent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Button, ExpandableText, Checkbox, Card, RiskProgressBar } from 'ui-components';
import { logEventWithChosenProductType, logSubmitRiskLevel } from '../../model/events';
import ProductWizardState from '../../model/ProductWizardState';
import { productWizardActions } from '../../model/store';
import LabelWithTextRiskValue from '../LabelWithTextRiskValue/LabelWithTextRiskValue';
import { ProductWizardTitle } from '../ProductWizard/ProductWizardTitle/ProductWizardTitle';
import Screen from '../ProductWizard/Screen';
import { useTransRedirect } from 'common-react';

import useStyles from './ChooseRiskLevel.style';
interface DisplayRisk {
  readonly risk: Risk;
  readonly enabled: boolean;
  readonly selected: boolean;
}

function isFundInRisk(fund: Fund, risk: Risk): boolean {
  return fund.risk.value === risk.value;
}

function filterFundsByRisk(funds: Fund[], risk: Risk): Fund[] {
  return funds.filter(fund => isFundInRisk(fund, risk));
}

function mapRisks(risks: Risk[], funds: Fund[], selectedRisks: Risk[]): DisplayRisk[] {
  return risks.map(risk => ({
    risk: risk,
    enabled: filterFundsByRisk(funds, risk).length !== 0,
    selected: selectedRisks.map(r => r.value).includes(risk.value)
  }));
}

const ChooseRiskLevel: FunctionComponent<{
}> = () => {

  const funds = useSelector((state: ProductWizardState) => state.apiState.funds);
  const risks = useSelector((state: ProductWizardState) => state.apiState.risks);
  const productType = useSelector((state: ProductWizardState) => state.uiState.productType);
  const selectedRisks = useSelector((state: ProductWizardState) => state.uiState.risks);
  const redirect = useTransRedirect();
  const c = useStyles({});
  const [t] = useTranslation('choose-risk-level');

  const dispatch = useDispatch();
  function selectRisk(risk: Risk) {
    dispatch(productWizardActions.addRisk(risk));
  }
  function deselectRisk(risk: Risk) {
    dispatch(productWizardActions.removeRisk(risk));
  }

  useEffect(() => logEventWithChosenProductType(EventType.preferenceStart) ,[]);

  if (productType === undefined) {
    return <Redirect to="/"/>;
  }

  const filteredFunds = funds.filter(fund => fund.quotesForUnitCode.hasOwnProperty(PRODUCTS[productType].unitCategory));
  const displayRisks = mapRisks(risks, filteredFunds, selectedRisks);

  const handleGoToNextStep = () => {
    logSubmitRiskLevel();
    redirect(Screen.CHOOSE_INVESTMENT_AMOUNT.code);
  }

  return (
    <div className={c.root}>
      <ProductWizardTitle titleKey={Screen.CHOOSE_RISK_LEVEL.code} subtitle={t('select-tip')}/>
      <div className={c.container}>
        {displayRisks.map( displayRisk =>
          <>
            {displayRisk.enabled && (
              <ButtonBase
                component="div"
                tabIndex={-1}
                disableRipple
                disableTouchRipple
                key={displayRisk.risk.value}
                onClick={() => (displayRisk.selected ? deselectRisk(displayRisk.risk) : selectRisk(displayRisk.risk))}
              >
                <Card
                  hoverable
                  className={clsx(c.riskItemWrapper, displayRisk.selected && c.riskItemWrapperSelected)}
                >
                  <FormControlLabel
                    onClick={e => e.stopPropagation()}
                    labelPlacement="end"
                    control={<Checkbox
                              id={`checkbox-${displayRisk.risk.value}`}
                              disabled={!displayRisk.enabled}
                              checked={displayRisk.selected}
                              value={displayRisk.risk.value}
                              onChange={(_, isChecked) => (isChecked ? selectRisk(displayRisk.risk) : deselectRisk(displayRisk.risk))}/>
                            }
                    label={<LabelWithTextRiskValue
                              labelText={displayRisk.risk.filterLabel}
                              riskLabelValue={displayRisk.risk.value}
                              htmlFor={`checkbox-${displayRisk.risk.value}`}/>
                          }
                  />
                  <RiskProgressBar
                    riskValue={displayRisk.risk.value}
                    size='big'
                  />
                </Card>
              </ButtonBase>
            )}
          </>)}
        {selectedRisks.length > 0 &&
          <Button
            color="secondary"
            accent
            variant="contained"
            buttonVariant="large"
            className={c.button}
            onClick={handleGoToNextStep}
          >
            {t('next')}
          </Button>
        }
        <Typography variant="body2" display="block" className={c.caption}>
          <ExpandableText shortText={t('short-caption')} longText={t('long-caption')}/>
        </Typography>
      </div>
    </div>
  );
};

export default ChooseRiskLevel;
